import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import { Search, X } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

interface SearchInputProps {
	placeholder?: string;
	delay?: number;
	className?: string;
	size?: 'default' | 'lg';
	/** Quando false, não usa `?search=` — só estado local/controlado. */
	syncToUrl?: boolean;
	value?: string;
	onValueChange?: (value: string) => void;
}

const SearchInput = ({
	placeholder = 'Pesquisar ...',
	delay = 300,
	className = '',
	size = 'default',
	syncToUrl = true,
	value: controlledValue = '',
	onValueChange,
}: SearchInputProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [internalTerm, setInternalTerm] = useState(() =>
		syncToUrl ? searchParams.get('search') || '' : controlledValue,
	);

	const searchTerm = syncToUrl ? internalTerm : controlledValue;
	const debouncedSearchTerm = useDebounce(searchTerm, delay);

	const setSearchTerm = useCallback(
		(next: string) => {
			if (syncToUrl) {
				setInternalTerm(next);
				return;
			}
			onValueChange?.(next);
		},
		[syncToUrl, onValueChange],
	);

	useEffect(() => {
		if (!syncToUrl) return;

		const nextSearch = debouncedSearchTerm.trim();

		setSearchParams(
			(prev) => {
				const currentSearch = (prev.get('search') ?? '').trim();
				if (nextSearch === currentSearch) return prev;

				const params = new URLSearchParams(prev);
				if (nextSearch) params.set('search', nextSearch);
				else params.delete('search');
				return params;
			},
			{ replace: true },
		);
	}, [debouncedSearchTerm, setSearchParams, syncToUrl]);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchTerm(e.target.value);
		},
		[setSearchTerm],
	);

	const handleClear = useCallback(() => {
		setSearchTerm('');
	}, [setSearchTerm]);

	return (
		<div
			className={cn(
				'relative h-fit w-full overflow-hidden rounded-full',
				size === 'lg' ? 'md:max-w-xl' : 'md:max-w-96',
				className,
			)}
		>
			<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
				<Search
					className={cn(
						size === 'lg' ? 'size-5' : 'w-4',
						'text-stone-400',
					)}
				/>
			</div>
			<input
				type="text"
				value={searchTerm}
				onChange={handleChange}
				placeholder={placeholder}
				className={cn(
					'block w-full rounded-full border-2 border-zinc-100 bg-white font-medium leading-5 text-stone-900 shadow-sm transition-all placeholder:text-zinc-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-stone-900/10',
					size === 'lg'
						? 'h-12 pl-12 pr-12 text-[15px]'
						: 'h-11 pl-11 pr-12',
				)}
			/>
			{searchTerm && (
				<button
					type="button"
					onClick={handleClear}
					className="absolute inset-y-0 right-0 flex items-center pr-4 text-zinc-400 transition-colors hover:text-stone-900"
				>
					<X className="w-4" />
				</button>
			)}
		</div>
	);
};

export { SearchInput };
